import React from "react";
import {Box, MenuItem, TextField} from "@mui/material";

export interface FormationAddress {
    street1: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
}

export interface FormationPerson {
    kind: "individual" | "entity";
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
    entityName: string;
    address: FormationAddress;
}

export const newAddress = (): FormationAddress => ({street1: "", street2: "", city: "", state: "FL", zipCode: "", country: "US"});
export const newPerson = (): FormationPerson => ({kind: "individual", firstName: "", middleName: "", lastName: "", suffix: "", entityName: "", address: newAddress()});

export function AddressFields({value, onChange, florida = false}: {
    value: FormationAddress;
    onChange: (value: FormationAddress) => void;
    florida?: boolean;
}) {
    const fields: [keyof FormationAddress, string, boolean][] = [
        ["street1", "Street address", true], ["street2", "Apartment / suite", false],
        ["city", "City", true], ["state", "State / province", value.country === "US"],
        ["zipCode", "ZIP / postal code", value.country === "US"], ["country", "Country (two-letter code)", true],
    ];
    return <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, gap: 2, my: 2}}>
        {fields.map(([key, label, required]) => <TextField key={key} label={label} required={required}
            value={value[key]} inputProps={{maxLength: key === "country" ? 2 : key === "zipCode" ? 20 : 255,
                readOnly: florida && (key === "state" || key === "country")}}
            onChange={event => onChange({...value, [key]: ["country", "state"].includes(key) ? event.target.value.toUpperCase() : event.target.value})}/>) }
    </Box>;
}

export function PersonFields({value, onChange, florida = false, allowEntity = true, disabled = false}: {
    value: FormationPerson;
    onChange: (value: FormationPerson) => void;
    florida?: boolean;
    allowEntity?: boolean;
    disabled?: boolean;
}) {
    return <>
        <TextField select disabled={disabled} label="Individual or business entity" value={value.kind} fullWidth sx={{mt: 2}}
            onChange={event => onChange({...value, kind: event.target.value as FormationPerson["kind"]})}>
            <MenuItem value="individual">Individual</MenuItem>
            {allowEntity && <MenuItem value="entity">Business entity</MenuItem>}
        </TextField>
        {value.kind === "entity" ? <TextField required fullWidth label="Business entity name" value={value.entityName}
            sx={{mt: 2}} inputProps={{maxLength: 255}} onChange={event => onChange({...value, entityName: event.target.value})}/>
            : <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, gap: 2, mt: 2}}>
                {([['firstName', 'First name'], ['lastName', 'Last name'], ['middleName', 'Middle name / initial'], ['suffix', 'Suffix']] as const)
                    .map(([key, label]) => <TextField key={key} label={label} required={key === "firstName" || key === "lastName"}
                        inputProps={{maxLength: key === "suffix" ? 20 : 255}} value={value[key]}
                        onChange={event => onChange({...value, [key]: event.target.value})}/>)}
            </Box>}
        <AddressFields value={value.address} onChange={address => onChange({...value, address})} florida={florida}/>
    </>;
}
